import React from "react";
import { PROFILE, STATS } from "../../mock";
import { ArrowDown, BookOpen, Heart, GraduationCap } from "lucide-react";
import { Button } from "../ui/button";

export default function Hero() {
  return (
    <section id="top" className="relative pt-28 pb-20 overflow-hidden">
      {/* Vibrant colorful blobs as decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-teal-soft opacity-50" />
        <div className="blob-saffron w-[420px] h-[420px] -top-32 -left-20" />
        <div className="blob-teal w-[460px] h-[460px] top-1/2 -right-32" />
        <div className="blob-coral w-[300px] h-[300px] bottom-10 left-1/4" />
        <div className="blob-indigo w-[260px] h-[260px] top-10 left-1/3 opacity-20" />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left text */}
        <div className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs tracking-[0.3em] uppercase text-gold font-medium">Est. 1951 · Mayyanad, Kerala</span>
          </div>
          <h1 className="font-display text-[44px] sm:text-6xl lg:text-7xl leading-[1.05] text-emerald-deep font-semibold">
            Prof. Dr. Shaji <br />
            <span className="italic font-normal text-stone-800">Prabhakaran</span>
          </h1>
          <p className="mt-4 text-stone-600 text-base sm:text-lg max-w-2xl">{PROFILE.honorific}</p>
          <p className="mt-3 font-serif-accent text-2xl text-stone-700 italic">{PROFILE.tagline}</p>

          <blockquote className="mt-8 border-l-4 border-gold pl-5 py-2 max-w-xl bg-cream/50">
            <p className="font-serif-accent text-stone-700 italic text-lg">“{PROFILE.motto}”</p>
            <p className="text-xs uppercase tracking-widest text-emerald-deep mt-1">{PROFILE.mottoMeaning}</p>
          </blockquote>

          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#about"><Button className="bg-emerald-deep hover:bg-[#1F2A3F] text-ivory rounded-full px-7 h-12">
              <BookOpen className="w-4 h-4 mr-2" /> Read his story
            </Button></a>
            <a href="#contact"><Button variant="outline" className="border-stone-300 text-stone-700 hover:border-emerald-deep hover:text-emerald-deep rounded-full px-7 h-12">
              <Heart className="w-4 h-4 mr-2" /> Get in touch
            </Button></a>
          </div>

          <div className="mt-10 flex items-center gap-3 text-stone-500">
            <ArrowDown className="w-4 h-4 animate-bounce" />
            <span className="text-xs uppercase tracking-widest">Scroll to explore</span>
          </div>
        </div>

        {/* Right portrait */}
        <div className="lg:col-span-5 relative fade-up fade-up-2">
          <div className="relative max-w-md mx-auto">
            <div className="absolute -inset-4 border border-gold/40 rounded-[28px] rotate-[2deg]" />
            <div className="absolute -inset-2 border border-gold/60 rounded-[24px] -rotate-[1deg]" />
            <div className="premium-frame">
              <div className="inner relative">
                <img src={PROFILE.images.primary} alt={PROFILE.name} className="w-full aspect-[4/5] object-cover" style={{ objectPosition: "center 25%" }} />
                <div className="absolute top-4 left-4 bg-emerald-deep/90 backdrop-blur text-ivory rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] border border-champagne/30">
                  <span className="text-champagne">●</span> Currently Practising
                </div>
              </div>
            </div>
            <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 bg-ivory rounded-2xl shadow-2xl p-4 flex items-center gap-3 border border-champagne/40 max-w-[320px] w-[88%]">
              <div className="w-12 h-12 rounded-full bg-emerald-deep flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-champagne" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Senior Consultant Neurologist</div>
                <div className="font-display text-emerald-deep text-sm leading-tight truncate">Aaraamam, Pongumoodu, TVPM</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-20">
        <div className="rounded-3xl px-6 py-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 shadow-2xl relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #064E3B 0%, #0D9488 50%, #1E1B4B 100%)" }}>
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-saffron/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          {STATS.map((s, i) => (
            <div key={i} className="text-center text-ivory relative">
              <div className="font-display text-3xl md:text-4xl text-champagne">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-ivory/85 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
